import React, { useEffect, useState } from 'react';
import Navbar from './Navbar';
import "./shopDashboard.css";
import { BACKEND_URL } from '../../services/Helper';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ReelCreator from './ReelCreator';

const ShopDashboard = () => {
  const navigate = useNavigate();
  const [shopName, setShopName] = useState('');
  const [shopData, setShopData] = useState(null);
  const [reelLink, setReelLink] = useState('');
  const [showReelCreator, setShowReelCreator] = useState(false);
  const [reelSaving, setReelSaving] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const fetchShop = async () => {
      try {
        const token = localStorage.getItem('shoplogintoken');
        const res = await fetch(`${BACKEND_URL}/nearbe/getshopdata`, {
          method: 'POST',
          headers: { Authorization: token },
        });
        const data = await res.json();
        if (res.ok) {
          const info = data.shopInformation || {};
          setReelLink(info.reelLink || '');
          setShopName(info.shopName || info.shopname || '');
          setShopData(info);
        }
      } catch { /* silent */ }
    };
    fetchShop();
  }, []);

  const saveReelLink = async () => {
    setReelSaving(true);
    try {
      const token = localStorage.getItem('shoplogintoken');
      const res = await fetch(`${BACKEND_URL}/nearbe/updatereellink`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ reelLink }),
      });
      if (res.ok) toast.success('Reel link saved!');
      else toast.error('Failed to save reel link');
    } catch {
      toast.error('Something went wrong!');
    } finally {
      setReelSaving(false);
    }
  };

  const verifyCoupon = async () => {
    if (!couponCode.trim()) { toast.error('Enter a coupon code'); return; }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const token = localStorage.getItem('shoplogintoken');
      const res = await fetch(`${BACKEND_URL}/nearbe/coupon/verify/${couponCode.trim().toUpperCase()}`, {
        headers: { Authorization: token },
      });
      const data = await res.json();
      setVerifyResult({ ok: res.ok, ...data });
    } catch {
      toast.error('Something went wrong!');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className='sd-page'>
      <Navbar />

      <div className='sd-body'>

        <div className='sd-greeting'>
          <h2>Welcome back{shopName ? `, ${shopName}` : ''}! 👋</h2>
          <p>Manage your shop, catalog, and customer interactions — all in one place.</p>
        </div>

        <div className='sd-grid'>

          {/* Quick Actions */}
          <div className='sd-card'>
            <div className='sd-card-title'>⚡ Quick Actions</div>
            <div className='sd-card-desc'>Jump to what you need</div>
            <div className='sd-actions'>
              <button className='sd-action-btn' onClick={() => navigate('/shopowner/shopdashboard/menulist')}>
                <span className='sd-action-icon'>🍽️</span>
                <span className='sd-action-label'>Menu / Catalog</span>
              </button>
              <button className='sd-action-btn' onClick={() => navigate('/shopowner/shopdashboard/offer')}>
                <span className='sd-action-icon'>🏷️</span>
                <span className='sd-action-label'>Offers</span>
              </button>
            </div>
          </div>

          {/* Reel Link */}
          <div className='sd-card'>
            <div className='sd-card-title'>📽️ Shop Reel Link</div>
            <div className='sd-card-desc'>
              Paste an Instagram or YouTube reel URL. Customers see a "Watch Reel" button on your shop page.
            </div>
            <div className='sd-input-row'>
              <input
                type="url"
                className='sd-input'
                placeholder='https://www.instagram.com/reel/...'
                value={reelLink}
                onChange={(e) => setReelLink(e.target.value)}
              />
              <button className='sd-btn' onClick={saveReelLink} disabled={reelSaving}>
                {reelSaving ? 'Saving...' : 'Save'}
              </button>
              {reelLink && (
                <a href={reelLink} target='_blank' rel='noopener noreferrer' className='sd-preview-link'>
                  Preview ↗
                </a>
              )}
            </div>
          </div>

          {/* AI Reel Creator — full width */}
          <div className='sd-card sd-card-full' style={{ padding: 0, overflow: 'hidden' }}>
            <div className='sd-reel-banner'>
              <div className='sd-reel-banner-icon'>🎬</div>
              <div className='sd-reel-banner-content'>
                <h3>
                  AI Reel Creator
                  <span className='sd-reel-badge'>BETA</span>
                </h3>
                <p>
                  Generate stunning promotional reels for your shop using AI. Pick a template,
                  upload your product photos, and let AI create a video ready to post on Instagram in minutes.
                </p>
                <button
                  className='sd-reel-create-btn'
                  onClick={() => setShowReelCreator(true)}
                >
                  🎬 Create Reel
                </button>
              </div>
            </div>
          </div>

          {/* Post on Nearbeee — Coming Soon */}
          <div className='sd-card sd-card-full' style={{ padding: 0, overflow: 'hidden' }}>
            <div className='sd-post-banner'>
              <div className='sd-post-banner-icon'>📲</div>
              <div className='sd-post-banner-content'>
                <h3>
                  Post on Nearbeee
                  <span className='sd-post-soon-badge'>COMING SOON</span>
                </h3>
                <p>
                  Share your reels, offers, and updates directly to the Nearbeee feed.
                  Nearby customers will discover your shop without searching — your content
                  reaches them automatically.
                </p>
                <button className='sd-post-soon-btn' disabled>
                  🔔 Notify Me When Live
                </button>
              </div>
            </div>
          </div>

          {/* Coupon Verify — full width */}
          <div className='sd-card sd-card-full'>
            <div className='sd-card-title'>🎟️ Verify Customer Coupon</div>
            <div className='sd-card-desc'>
              Enter the code a customer shows you to redeem their discount.
            </div>
            <div className='sd-input-row'>
              <input
                type="text"
                className='sd-input sd-input-mono'
                placeholder='NB-XXXXXXXX'
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              />
              <button className='sd-btn' onClick={verifyCoupon} disabled={verifying}>
                {verifying ? 'Checking...' : 'Verify'}
              </button>
            </div>

            {verifyResult && (
              <div className={verifyResult.ok ? 'sd-verify-ok' : 'sd-verify-fail'}>
                {verifyResult.ok ? (
                  <>
                    <span className='sd-verify-icon'>✅</span>
                    <div className='sd-verify-body'>
                      <strong>Coupon Valid!</strong>
                      <p>Discount: <strong>{verifyResult.discountApplied}% OFF</strong></p>
                      <p>Offer: {verifyResult.coupon?.offer?.title}</p>
                      {verifyResult.coupon?.feedbackProvided && <p>⭐ Bonus rate — customer wrote a review</p>}
                      <p className='sd-verify-note'>Marked as used. Do not accept this code again.</p>
                    </div>
                  </>
                ) : (
                  <>
                    <span className='sd-verify-icon'>❌</span>
                    <div className='sd-verify-body'>
                      <strong>{verifyResult.message}</strong>
                      {verifyResult.usedAt && <p>Was used on: {new Date(verifyResult.usedAt).toLocaleString()}</p>}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {showReelCreator && (
        <ReelCreator
          shopToken={localStorage.getItem('shoplogintoken')}
          shopData={shopData}
          onClose={() => setShowReelCreator(false)}
          onReelSaved={(url) => { setReelLink(url); setShowReelCreator(false); }}
        />
      )}
    </div>
  );
};

export default ShopDashboard;
